import React, { useEffect, useMemo, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import { deleteDestination } from "helpers/destinations/deleteDestination";
import { deleteSource } from "helpers/Sources/deleteSource";
import { createTracker } from "helpers/trackers/createTracker";

import { getAvailableDestinationCodes } from "components/organisms/DestinationCreation/DestinationCreation.helpers";
import { ModalName } from "components/organisms/ModalRoot/ModalRoot.layout";
import SwalAlertComp from "components/SwalAlert";

import StepManager from "./components/StepManager";
import WizardFooter from "./components/WizardFooter";
import WizardHeader from "./components/WizardHeader";
import { WIZARD_MODAL_CONTEXT } from "./SignalWizard.constants";
import {
    buildTrackerPayload,
    isSourceSelected,
    normalizeIds,
    toggleSourceSelection,
} from "./SignalWizard.helpers";
import { Main, Shell } from "./SignalWizard.styled";
import { SignalWizardStep } from "./SignalWizard.types";

const isBlockingOverlayOpen = () =>
    Boolean(
        document.querySelector(
            ".MuiDialog-root, .MuiPopover-root, .swal2-container.swal2-shown",
        ),
    );

const SignalWizard = ({
    language,
    sources = [],
    sourcesStatus,
    sourcesTypes = [],
    destinations = [],
    destinationsStatus,
    template,
    preSelectedSourceId,
    preSelectedDestinationIds,
    isModalOpen,
    dispatchGetSources,
    dispatchGetDestinations,
    dispatchOpenModal,
    dispatchClearPreSelectedSourceId,
    dispatchClearPreSelectedDestinationIds,
    dispatchClearTemplate,
}) => {
    const navigate = useNavigate();

    const isTemplateMode = Boolean(template?.source || template?.destination);
    const isDestinationForcedByTemplate =
        isTemplateMode && !template?.isDestinationOptional;

    const [step, setStep] = useState(SignalWizardStep.SOURCES);
    const [selectedSources, setSelectedSources] = useState({});
    const [selectedDestinations, setSelectedDestinations] = useState([]);
    const [trackerName, setTrackerName] = useState("");
    const [trackerNameError, setTrackerNameError] = useState("");
    const [isCreatingTracker, setIsCreatingTracker] = useState(false);
    const [createdTrackerId, setCreatedTrackerId] = useState(null);

    const autoOpenedModalsRef = useRef({});
    const isModalOpenRef = useRef(isModalOpen);
    isModalOpenRef.current = isModalOpen;

    useEffect(() => {
        window.localStorage.removeItem("swo");
        dispatchGetSources();
        dispatchGetDestinations();
        return () => dispatchClearTemplate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedSourceItems = useMemo(
        () =>
            sources.filter((source) =>
                isSourceSelected(selectedSources, source),
            ),
        [sources, selectedSources],
    );

    const selectedDestinationItems = useMemo(
        () =>
            destinations.filter((destination) =>
                selectedDestinations.includes(+destination.id),
            ),
        [destinations, selectedDestinations],
    );

    const availableDestinationCodes = useMemo(
        () =>
            getAvailableDestinationCodes({
                sourcesTypes,
                sourceTypeCodes: selectedSourceItems.map(
                    (source) => source?.source_type?.code,
                ),
            }),
        [sourcesTypes, selectedSourceItems],
    );

    const isDestinationAvailable = (destination) =>
        !sourcesTypes.length ||
        availableDestinationCodes.includes(destination?.destination_type?.code);

    const exitWizard = () => navigate("/trackers");

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (
                event.key !== "Escape" ||
                isModalOpenRef.current ||
                isBlockingOverlayOpen()
            ) {
                return;
            }
            exitWizard();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreateSource = () =>
        dispatchOpenModal({
            modalName: ModalName.CREATE_SOURCE,
            data: {
                context: WIZARD_MODAL_CONTEXT,
                sourceType: template?.source || undefined,
            },
        });

    const openCreateDestination = () =>
        dispatchOpenModal({
            modalName: ModalName.CREATE_DESTINATION,
            data: {
                context: WIZARD_MODAL_CONTEXT,
                destinationType: template?.destination || undefined,
                sourceTypeCodes: selectedSourceItems.map(
                    (source) => source?.source_type?.code,
                ),
            },
        });

    // Template mode, or an empty list, starts straight in the create modal.
    useEffect(() => {
        if (isModalOpen || autoOpenedModalsRef.current[step]) return;

        if (step === SignalWizardStep.SOURCES) {
            const hasNoSources =
                sourcesStatus === "success" && sources.length === 0;
            if (isTemplateMode || hasNoSources) {
                autoOpenedModalsRef.current[step] = true;
                openCreateSource();
            }
        } else if (step === SignalWizardStep.DESTINATIONS) {
            const hasNoDestinations =
                destinationsStatus === "success" && destinations.length === 0;
            if (isDestinationForcedByTemplate || hasNoDestinations) {
                autoOpenedModalsRef.current[step] = true;
                openCreateDestination();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, sourcesStatus, destinationsStatus, isModalOpen]);

    // Select whatever the create modals (or the pages that redirect here) created.
    useEffect(() => {
        if (preSelectedSourceId == null || !sources.length) return;
        const ids = normalizeIds(preSelectedSourceId);
        const createdSources = sources.filter((source) =>
            ids.includes(+source.id),
        );
        if (!createdSources.length) return;

        setSelectedSources((prev) =>
            createdSources.reduce(
                (selection, source) =>
                    toggleSourceSelection(selection, source, true),
                prev,
            ),
        );
        dispatchClearPreSelectedSourceId();
        if (isTemplateMode && step === SignalWizardStep.SOURCES) {
            setStep(SignalWizardStep.DESTINATIONS);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preSelectedSourceId, sources]);

    useEffect(() => {
        if (!preSelectedDestinationIds?.length || !destinations.length) return;
        const ids = normalizeIds(preSelectedDestinationIds);
        const createdIds = destinations
            .filter((destination) => ids.includes(+destination.id))
            .map((destination) => +destination.id);
        if (!createdIds.length) return;

        setSelectedDestinations((prev) => [
            ...new Set([...prev, ...createdIds]),
        ]);
        dispatchClearPreSelectedDestinationIds();
        if (
            isDestinationForcedByTemplate &&
            step === SignalWizardStep.DESTINATIONS
        ) {
            setStep(SignalWizardStep.CREATE_TRACKER);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preSelectedDestinationIds, destinations]);

    const showResult = (response) =>
        SwalAlertComp({
            title: response.status
                ? (language?.delete_swal_modal?.success ?? "Success")
                : (language?.delete_swal_modal?.failed ?? "Failed"),
            message: response.message,
            icon: response.status ? "success" : "error",
            ...(response.status && { timer: 3000 }),
        });

    const handleDeleteSource = async (source) => {
        const response = await deleteSource(source.id);
        showResult(response);
        if (!response.status) return;
        dispatchGetSources();
        setSelectedSources((prev) =>
            toggleSourceSelection(prev, source, false),
        );
    };

    const handleDeleteDestination = async (destination) => {
        const response = await deleteDestination(destination.id);
        showResult(response);
        if (!response.status) return;
        dispatchGetDestinations();
        setSelectedDestinations((prev) =>
            prev.filter((id) => id !== +destination.id),
        );
    };

    const handleToggleSource = (source, checked) =>
        setSelectedSources((prev) =>
            toggleSourceSelection(prev, source, checked),
        );

    const handleToggleDestination = (destination, checked) =>
        setSelectedDestinations((prev) =>
            checked
                ? [...new Set([...prev, +destination.id])]
                : prev.filter((id) => id !== +destination.id),
        );

    const goToStep = (nextStep) => {
        setStep(nextStep);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const goToCreateTracker = () => {
        setSelectedDestinations(
            selectedDestinationItems
                .filter(isDestinationAvailable)
                .map((destination) => +destination.id),
        );
        goToStep(SignalWizardStep.CREATE_TRACKER);
    };

    const handleTrackerNameChange = (value) => {
        setTrackerName(value);
        setTrackerNameError("");
    };

    const handleCreateTracker = async () => {
        if (!trackerName.trim()) {
            setTrackerNameError(
                language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!",
            );
            return;
        }

        setIsCreatingTracker(true);
        try {
            const response = await createTracker(
                buildTrackerPayload({
                    selectedSources,
                    selectedDestinations,
                    trackerName,
                }),
            );
            if (!response.status) {
                showResult(response);
                return;
            }
            const trackerId = response.data?.tracker?.id;
            setCreatedTrackerId(trackerId);
            window.dataLayer?.push({ event: "trackerCreated", trackerId });
            goToStep(SignalWizardStep.SUCCESS);
        } finally {
            setIsCreatingTracker(false);
        }
    };

    const goToTracker = () =>
        navigate(`/tracker/${createdTrackerId}`, {
            state: { fromWizard: true },
        });

    const footerProps = {
        [SignalWizardStep.SOURCES]: {
            selectedCount: selectedSourceItems.length,
            totalCount: sources.length,
            itemLabel: language?.tracker_wizard?.sources ?? "sources",
            primaryLabel:
                language?.tracker_wizard?.continue_to_destinations ??
                "Continue to Destinations",
            primaryDisabled: !selectedSourceItems.length,
            primaryTestId: "wizard-continue",
            onPrimary: () => goToStep(SignalWizardStep.DESTINATIONS),
        },
        [SignalWizardStep.DESTINATIONS]: {
            selectedCount: selectedDestinationItems.length,
            totalCount: destinations.length,
            itemLabel: language?.tracker_wizard?.destinations ?? "destinations",
            backLabel:
                language?.tracker_wizard?.back_to_sources ?? "Back to Sources",
            onBack: isDestinationForcedByTemplate
                ? undefined
                : () => goToStep(SignalWizardStep.SOURCES),
            primaryLabel:
                language?.tracker_wizard?.continue_to_create_tracker ??
                "Continue to Create Tracker",
            primaryDisabled: !selectedDestinationItems.some(
                isDestinationAvailable,
            ),
            primaryTestId: "wizard-continue",
            onPrimary: goToCreateTracker,
        },
        [SignalWizardStep.CREATE_TRACKER]: {
            backLabel:
                language?.tracker_wizard?.back_to_destinations ??
                "Back to Destinations",
            onBack: isTemplateMode
                ? undefined
                : () => goToStep(SignalWizardStep.DESTINATIONS),
            backTestId: "wizard-back",
            primaryLabel:
                language?.tracker_wizard?.create_tracker ?? "Create Tracker",
            primaryTestId: "wizard-complete",
            isLoading: isCreatingTracker,
            onPrimary: handleCreateTracker,
        },
    }[step];

    const stepProps = {
        language,
        isTemplateMode,
        isDestinationForcedByTemplate,
        sources,
        isSourcesLoading: sourcesStatus === "loading" && !sources.length,
        selectedSources,
        selectedSourceItems,
        onToggleSource: handleToggleSource,
        onDeleteSource: handleDeleteSource,
        onAddSource: openCreateSource,
        destinations,
        isDestinationsLoading:
            destinationsStatus === "loading" && !destinations.length,
        selectedDestinations,
        selectedDestinationItems,
        isDestinationAvailable,
        onToggleDestination: handleToggleDestination,
        onDeleteDestination: handleDeleteDestination,
        onAddDestination: openCreateDestination,
        trackerName,
        trackerNameError,
        onTrackerNameChange: handleTrackerNameChange,
        onSubmitTracker: handleCreateTracker,
        onFinish: goToTracker,
    };

    if (step === SignalWizardStep.SUCCESS) {
        return (
            <Shell data-step={step}>
                <StepManager step={step} {...stepProps} />
            </Shell>
        );
    }

    return (
        <Shell data-step={step}>
            <WizardHeader
                language={language}
                step={step}
                onStepClick={isTemplateMode ? undefined : goToStep}
                onExit={exitWizard}
            />
            <Main>
                <StepManager step={step} {...stepProps} />
            </Main>
            <WizardFooter language={language} {...footerProps} />
        </Shell>
    );
};

export default SignalWizard;
